import React, { useEffect, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { Spinner, Alert } from "react-bootstrap";
import { useFeeScheduleList, useGetFundList } from "../../data/data";
import Cookies from "js-cookie";
import { formatLargeCurr, formateDataCurr } from "../../utils/types";
import { API_KEY, BASE_URL } from "../../api/api";
import { Button } from "../../components/Button/Button";
import { Pagination } from "../../components/Pagination/index";

const FeeSchedulesTable = () => {
  const [maxExceed, setMaxExceed] = useState<string>("");
  const [currentRowIndex, setCurrentRowIndex] = useState<number | null>(null);
  const [inputValues, setInputValues] = useState<any>({});
  const [showAdd, setShowAdd] = useState(false);
  const selectedClient: any = Cookies.get("client");
  const [submissionStatus, setSubmissionStatus] = useState<string>("");
  const [isShowLoading, setIsShowLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalRows, setTotalRows] = useState(0);
  const initiallyData = [
    {
      id: 1,
      disabled: false,
      feeType: "sliding",
      minValue: "0.00",
      maxValue: "",
      feeBps: "",
      entered: false,
      originalMinValue: "0.00",
      originalMaxValue: "",
    },
  ];
  const [rows, setRows] = useState<any>(initiallyData);
  const [recentlyAddedRow, setRecentlyAddedRow] = useState<number | null>(null);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);

  const handleChange = (event: any, index: number) => {
    const { name, value } = event.target;
    const updatedRows = [...rows];

    if (name === "feeType") {
      if (value === "fixed") {
        updatedRows[index] = {
          ...updatedRows[index],
          feeType: value,
          minValue: "0.00",
          maxValue: "999999999999.99",
          disabled: false,
        };
      } else if (value === "SlidingFixed") {
        const newMinValue =
          index > 0
            ? (parseFloat(rows[index - 1].maxValue) + 0.01).toFixed(2)
            : "0.00";
        updatedRows[index] = {
          ...updatedRows[index],
          feeType: value,
          minValue: newMinValue,
          maxValue: "999999999999.99",
          disabled: false,
        };
      } else if (value === "FixedSliding") {
        updatedRows[index] = {
          ...updatedRows[index],
          feeType: value,
          minValue: "0.00",
          maxValue: "",
          disabled: false,
        };
      } else {
        const newMinValue =
          index > 0
            ? (parseFloat(rows[index - 1].maxValue) + 0.01).toFixed(2)
            : "0.00";
        updatedRows[index] = {
          ...updatedRows[index],
          feeType: value,
          minValue: newMinValue,
          maxValue: "",
          disabled: false,
        };
      }
    } else {
      updatedRows[index][name] = value;
      updatedRows[index].entered = value !== "";

      if (updatedRows[index].feeType !== "fixed") {
        updatedRows[index].originalMinValue = updatedRows[index].minValue;
        updatedRows[index].originalMaxValue = updatedRows[index].maxValue;
      }
    }

    setInputValues({
      ...inputValues,
      [index]: {
        ...inputValues[index],
        [name]: value,
      },
    });
    setCurrentRowIndex(index);
    setRows(updatedRows);
    validateForm(updatedRows);
  };

  const validateInput = (index: any, name: any) => {
    const value = inputValues[index]?.[name];
    return parseFloat(value) < 0;
  };

  const validateMaxValue = (index: any) => {
    const { maxValue: currentMaxValue } = inputValues[index] || {};
    const { maxValue: prevMaxValue } = inputValues[index - 1] || {};
    return index > 0 && parseFloat(currentMaxValue) < parseFloat(prevMaxValue);
  };

  const validateMinValue = (index: any) => {
    const { minValue, maxValue } = inputValues[index] || {};
    return parseFloat(minValue) > parseFloat(maxValue);
  };

  const validateFeeBps = (index: any) => {
    const value = inputValues[index]?.feeBps;
    return parseInt(value) > 300;
  };

  const validateForm = (rows: any) => {
    const isValid = rows.every((row: any, index: number) => {
      const isMinValid =
        !validateInput(index, "minValue") && !validateMinValue(index);
      const isMaxValid =
        !validateInput(index, "maxValue") && !validateMaxValue(index);
      const isFeeBpsValid = !validateFeeBps(index) && row.feeBps !== "";
      return isMinValid && isMaxValid && isFeeBpsValid;
    });

    setIsFormValid(isValid);
  };

  useEffect(() => {
    validateForm(rows);
  }, [rows]);

  const handleRemoveRow = (index: number) => {
    const updatedRows = [...rows];
    updatedRows.splice(index, 1);

    if (index > 0 && updatedRows[index - 1]) {
      updatedRows[index - 1].disabled = false;
    }

    setRows(updatedRows);
    setRecentlyAddedRow(null);
    validateForm(updatedRows);
  };

  const handleAddRow = () => {
    const sumMaxValue = rows.reduce(
      (acc: any, curr: any) => acc + parseFloat(curr.maxValue || 0),
      0
    );
    const newMaxValue = parseFloat(rows[rows.length - 1].maxValue || 0);

    if (sumMaxValue + newMaxValue >= 1000000000000.0) {
      setMaxExceed("Cannot add new row, maximum threshold exceeded.");
      console.log("Cannot add new row, maximum threshold exceeded.");
      return;
    } else {
      setMaxExceed("");
    }

    const newId = rows.length + 1;
    const newMinValue = (newMaxValue + 0.01).toFixed(2);

    const newRow = {
      id: newId,
      disabled: false,
      feeType: "sliding",
      minValue: newMinValue,
      maxValue: "",
      feeBps: "",
      entered: false,
      originalMinValue: newMinValue,
      originalMaxValue: "",
    };

    const updatedRows = rows.map((row: any) => ({
      ...row,
      disabled: true,
    }));

    updatedRows.push(newRow);

    setRows(updatedRows);
    setRecentlyAddedRow(newId);
    validateForm(updatedRows);
  };

  const constructPayload = () => {
    const payload = rows.map((row: any) => {
      let adjustedFeeType = row.feeType;
      if (row.feeType === "fixed") {
        adjustedFeeType = "Fixed";
      } else if (row.feeType === "sliding") {
        adjustedFeeType = "Sliding";
      }

      return {
        SlideID: row.id,
        feeType: adjustedFeeType,
        minValue: parseFloat(row.minValue),
        maxValue: parseFloat(row.maxValue),
        feeBps: parseInt(row.feeBps),
      };
    });

    return payload;
  };

  const handleSubmit = async () => {
    setIsShowLoading(true);
    const payload = constructPayload();
    console.log(payload);
    try {
      const response = await fetch(`${BASE_URL}/${API_KEY}/api/feeschedules`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Cookies.get("newJwt")}`,
          ClientID: selectedClient,
        },
        body: JSON.stringify(payload),
      });
      console.log(response);
      if (response.status === 204) {
        setSubmissionStatus("Fee Schedule Created successfully.");
        setRows(initiallyData);
        setIsShowLoading(false);
        setShowAdd(false);
        refetch();
        setTimeout(() => {
          setSubmissionStatus("");
        }, 3000);
      } else {
        const errorData = await response.json();
        console.log(errorData);
        setIsShowLoading(false);
        setSubmissionStatus(errorData?.errors?.[0]?.message);
      }
    } catch (error: any) {
      console.log(error);
      setIsShowLoading(false);
      setSubmissionStatus("Submission failed.");
    }
  };
  const [columns, setColumns] = useState([
    {
      headerName: "FeeSchedule ID",
      field: "feeScheduleID",
      cellClass: "left",
      flex: 1,
    },
    {
      headerName: "Fee Type",
      field: "feeScheduleType",
      sortable: true,
      flex: 1,
    },
    {
      headerName: "Min Amount	",
      field: "minAmount",
      sortable: true,
      flex: 1,
      cellRenderer: formateDataCurr,
    },
    {
      headerName: "Max Amount	",
      field: "maxAmount",
      sortable: true,
      cellRenderer: formatLargeCurr,
      flex: 1,
    },
    {
      headerName: "Fee Bps",
      field: "feeBps",
      sortable: true,
      width: 70,
      cellClass: "cell-wrap-text textAlign",
    },
    {
      headerName: "Slide ID",
      field: "slideID",
      sortable: true,
      width: 100,
    },
  ]);
  const { data, isLoading, isFetching, refetch } = useFeeScheduleList(
    "feeschedule",
    currentPage,
    parseInt(selectedClient),
    Cookies.get("newJwt")
  );
  console.log(pageSize, currentPage);

  function handleClose() {
    setShowAdd((old) => !old);
    setSubmissionStatus("");
    setRows(initiallyData);
  }

  useEffect(() => {
    if (data) {
      setTotalRows(data?.total);
    }
  }, [data]);

  const totalPages = Math.ceil(totalRows / pageSize);
  console.log(totalRows, totalPages, data);

  return (
    <div className="position-relative">
      {submissionStatus && (
        <Alert
          variant={submissionStatus.includes("must be") ? "danger" : "success"}
          className="mt-2 px-4 py-2 opacity-100 position-absolute"
          style={{
            left: "-12px",
            top: "-30px",
            zIndex: 9,
            width: "100%",
            transition: "opacity 0.5s ease-in-out",
          }}
        >
          {submissionStatus}
        </Alert>
      )}
      <div className="row w-100 card p-4">
        <div className="">
          <div className="d-flex justify-content-end mb-4">
            <Button
              onClick={handleClose}
              className="btn btn-primary"
              size={""}
              children={showAdd ? "Close" : "Add"}
            />
          </div>

          {showAdd ? (
            <div className="pb-5 d-block">
              <div className="card mr-2 shadow-none position-relative top-4 mb-10">
                {maxExceed ? (
                  <div
                    className="alert alert-danger position-absolute p-2"
                    style={{ top: "-40px", width: "100%" }}
                    role="alert"
                  >
                    {maxExceed}
                  </div>
                ) : null}

                <div className="card-body p-0">
                  <div className="chart chart-xs">
                    <div className="chartjs-size-monitor">
                      <div
                        className="chartjs-size-monitor-shrink row"
                        style={{ height: "300px" }}
                      >
                        <table
                          id="UpdateTargets"
                          className="table table-striped my-0 table-fixed"
                        >
                          <thead>
                            <tr
                              style={{
                                height: "42px",
                                verticalAlign: "middle",
                              }}
                            >
                              <th
                                className="d-none d-xl-table-cell"
                                style={{ paddingLeft: "35px" }}
                              >
                                Fee Type
                              </th>
                              <th
                                className="d-none d-xl-table-cell  text-end"
                                style={{ paddingRight: "50px" }}
                              >
                                Min Amount
                              </th>
                              <th
                                className="d-none d-xl-table-cell  text-end"
                                style={{ paddingRight: "54px" }}
                              >
                                Max Amount
                              </th>
                              <th
                                className="d-none d-xl-table-cell  text-end"
                                style={{ paddingRight: "54px" }}
                              >
                                FeeBps
                              </th>
                              <th
                                className="d-none d-xl-table-cell"
                                style={{ width: "100px" }}
                              >
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody style={{ overflowY: "auto", height: "300px" }}>
                            {rows.map((row: any, index: any) => (
                              <tr key={row.id} style={{ height: "65px" }}>
                                <td className="d-none d-xl-table-cell px-4">
                                  <select
                                    className="form-select form-control secure-input-field"
                                    value={row.feeType}
                                    onChange={(event) =>
                                      handleChange(event, index)
                                    }
                                    name="feeType"
                                  >
                                    <option value="fixed">Fixed</option>
                                    <option value="sliding">Sliding</option>
                                    {/* <option value="SlidingFixed">Sliding Fixed</option>
                            <option value="FixedSliding">Fixed Sliding</option> */}
                                  </select>
                                </td>
                                <td className="d-none d-xl-table-cell px-3 position-relative text-end">
                                  <input
                                    className="form-control secure-input-field text-end"
                                    type="number"
                                    value={parseFloat(row.minValue).toFixed(2)}
                                    onChange={(event) =>
                                      handleChange(event, index)
                                    }
                                    name="minValue"
                                    min={0}
                                    step="0.01"
                                    readOnly={
                                      index === 0 ||
                                      row.feeType === "fixed" ||
                                      row.disabled
                                    }
                                  />
                                  {(validateInput(index, "minValue") ||
                                    validateMinValue(index)) && (
                                    <small
                                      className="position-absolute"
                                      style={{
                                        color: "red",
                                        right: "16px",
                                        bottom: "-1.5em",
                                      }}
                                    >
                                      {validateInput(index, "minValue") && (
                                        <div>
                                          Please enter a non-negative value.
                                        </div>
                                      )}
                                      {validateMinValue(index) && (
                                        <div>
                                          Min value must be less than or equal
                                          to max value.
                                        </div>
                                      )}
                                    </small>
                                  )}
                                </td>
                                <td className="d-none d-xl-table-cell px-3 position-relative text-end">
                                  <input
                                    className="form-control secure-input-field text-end"
                                    type="number"
                                    value={row.maxValue}
                                    onChange={(event) =>
                                      handleChange(event, index)
                                    }
                                    name="maxValue"
                                    min={0}
                                    step="0.01"
                                    readOnly={
                                      row.feeType === "fixed" || row.disabled
                                    }
                                  />
                                  {(validateInput(index, "maxValue") ||
                                    validateMaxValue(index)) && (
                                    <small
                                      className="position-absolute"
                                      style={{
                                        color: "red",
                                        right: "16px",
                                        bottom: "-1.5em",
                                      }}
                                    >
                                      {validateInput(index, "maxValue") && (
                                        <div>
                                          Please enter a non-negative value.
                                        </div>
                                      )}
                                      {validateMaxValue(index) && (
                                        <div>
                                          Max value must be greater than or
                                          equal to min value.
                                        </div>
                                      )}
                                    </small>
                                  )}
                                </td>
                                <td className="d-none d-xl-table-cell px-3 position-relative text-end">
                                  <input
                                    className="form-control secure-input-field text-end"
                                    type="number"
                                    value={row.feeBps}
                                    onChange={(event) =>
                                      handleChange(event, index)
                                    }
                                    name="feeBps"
                                    min={1}
                                    readOnly={row.disabled}
                                  />
                                  {(parseInt(row.feeBps) <= 0 ||
                                    validateFeeBps(index)) && (
                                    <small
                                      className="position-absolute"
                                      style={{
                                        color: "red",
                                        right: "16px",
                                        bottom: "0em",
                                      }}
                                    >
                                      {parseInt(row.feeBps) <= 0 && (
                                        <div>
                                          feeBps should be greater than zero
                                        </div>
                                      )}
                                      {validateFeeBps(index) && (
                                        <div>Fee Bps should not exceed 300</div>
                                      )}
                                    </small>
                                  )}
                                </td>
                                <td
                                  className="d-none d-xl-table-cell px-2 position-relative text-center"
                                  style={{ width: "100px" }}
                                >
                                  <div className="d-flex">
                                    <div style={{ width: "50px" }}>
                                      {index === rows.length - 1 &&
                                        row.maxValue &&
                                        row.feeBps && // Check if feeBps has a valid value
                                        !row.disabled && (
                                          <button
                                            className="btn"
                                            onClick={handleAddRow}
                                            disabled={
                                              row.feeType === "fixed" ||
                                              !row.feeBps // Only enable button if feeBps is entered
                                            }
                                          >
                                            <i className="align-middle fas fa-add"></i>
                                          </button>
                                        )}
                                    </div>
                                    <div style={{ width: "50px" }}>
                                      {index !== 0 && (
                                        <button
                                          className="btn"
                                          onClick={() => handleRemoveRow(index)}
                                        >
                                          <i className="align-middle fas fa-trash"></i>
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row pt-5 mt-10">
                <div className="float-end text-end">
                  <Button
                    onClick={handleClose}
                    className="btn btn-link"
                    size={"small"}
                    children={"Cancel"}
                  />
                  <Button
                    onClick={handleSubmit}
                    disabled={!isFormValid}
                    className="btn btn-primary"
                    size={"small"}
                    children={"Submit"}
                    showLoading={isShowLoading}
                  />
                </div>
              </div>
            </div>
          ) : null}

          {isLoading || isFetching ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ height: "500px" }}
            >
              <Spinner />
            </div>
          ) : (
            <>
              <div
                className="ag-theme-alpine position-relative"
                style={{
                  height: "500px",
                  width: "100%",
                }}
              >
                <AgGridReact
                  rowData={data?.data ?? []}
                  columnDefs={columns}
                  animateRows={true}
                  domLayout={"normal"}
                  pagination={false}
                  paginationPageSize={20}
                ></AgGridReact>
              </div>
            </>
          )}
          <div className="mt-4">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export { FeeSchedulesTable };
